import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatOption } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TilbyDatetimePickerComponent } from '@tilby/tilby-ui-lib/components/tilby-magic-form';
import { TilbyCurrencyPipe } from '@tilby/tilby-ui-lib/pipes/tilby-currency';
import { $state } from 'app/ajs-upgraded-providers';
import { ConfigurationManagerService, EnvironmentInfoService, LoaderService } from 'src/app/core';
import { AlertDialogService, ConfirmDialogService, PromptDialogService } from 'src/app/dialogs';
import { TruncatedTextDirective } from 'src/app/directives/truncated-text.directive';
import { TrimInputDirective } from 'src/app/directives/trim.directive';
import { BarcodeComponent } from 'src/app/shared/components';
import { ActionBtnComponent } from 'src/app/shared/components/master-detail-layout/action-btn/action-btn.component';
import { MathUtils } from 'src/app/shared/utils';
import {
  isNewMovementErrorCode,
  MovementEntry,
  NewMovementFormGroup,
  NewMovementMode,
  NewMovementRowValue,
  PreparedMovement,
  SALES_LOCATION_ID,
  STOCK_TRANSFER_CAUSE,
  SUPPLIERS_ORDERS_LOCATION_ID
} from './models/stock-new-movement.model';
import { StockNewMovementDataService } from './services/stock-new-movement-data.service';
import { StockNewMovementFormService } from './services/stock-new-movement-form.service';
import { StockNewMovementSaveService } from './services/stock-new-movement-save.service';

const MOVEMENTS_STATE = 'app.new.stock.movements';

@Component({
  selector: 'app-stock-new-movement',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, TranslateModule,
    MatFormFieldModule, MatInputModule, MatSelectModule, MatAutocompleteModule,
    MatButtonModule, MatIconModule,
    TilbyDatetimePickerComponent, TruncatedTextDirective, TrimInputDirective,
    ActionBtnComponent, BarcodeComponent
  ],
  providers: [StockNewMovementFormService, StockNewMovementDataService, StockNewMovementSaveService],
  host: { class: 'tw-block tw-h-full' },
  templateUrl: './stock-new-movement.component.html',
  styleUrl: './stock-new-movement.component.scss'
})
export class StockNewMovementComponent implements OnInit {

  protected readonly formService = inject(StockNewMovementFormService);
  protected readonly dataService = inject(StockNewMovementDataService);
  private readonly saveService = inject(StockNewMovementSaveService);
  private readonly state = inject($state);
  private readonly translateService = inject(TranslateService);
  private readonly loaderService = inject(LoaderService);
  private readonly alertDialogService = inject(AlertDialogService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly promptDialogService = inject(PromptDialogService);
  private readonly configurationManagerService = inject(ConfigurationManagerService);
  private readonly environmentInfoService = inject(EnvironmentInfoService);
  private readonly tilbyCurrencyPipe = inject(TilbyCurrencyPipe);

  @Input() mode?: NewMovementMode | null;
  @Input() movements?: any;
  @Input() stockLocationId?: number | null;

  // @ViewChildren('entryInput') private entryInputs!: QueryList<ElementRef<HTMLInputElement>>;

  protected form!: NewMovementFormGroup;

  protected readonly currencySymbol = TilbyCurrencyPipe.currency.symbol_native;
  protected readonly showUnitCount = !!this.configurationManagerService.getPreference('stock_movements.show_unit_count' as any);
  protected readonly canUseCameraBarcode = this.environmentInfoService.isCameraBarcodeSupported();

  async ngOnInit() {
    this.loaderService.show();

    try {
      await this.dataService.load();
    } catch (error) {
      this.alertDialogService.openDialog({ data: { messageLabel: 'STOCK.NEW_MOVEMENT.UNEXPECTED_ERROR' } });
      return this.goToMovements();
    } finally {
      this.loaderService.hide();
    }

    this.form = this.formService.createForm(this.stockLocationId || SALES_LOCATION_ID);
    this.initFromParams();
  }

  // ------------------- inizializzazione -------------------

  private initFromParams() {
    const mode = this.mode ?? null;
    this.formService.setMode(mode);

    if (mode === 'supplier_order') {
      this.formService.movementCause.setValue(this.formService.findCauseByValue('supplier_order'));
      this.formService.sourceLocationId.setValue(SUPPLIERS_ORDERS_LOCATION_ID);
    }

    let movements = this.movements ? structuredClone(this.movements) : [];
    movements = Array.isArray(movements) ? movements : [movements];

    if (!movements.length) {
      return this.formService.addRow();
    }

    movements.forEach((movement: any) => this.formService.addRow({
      entry: this.dataService.getEntryForMovement(movement),
      quantity: movement.quantity,
      price: movement.price,
      perc_discount: movement.perc_discount
    }));

    this.setupCommonDefaults(movements[0]);

    if (mode === 'goods_delivery') {
      this.formService.setMode(mode);

      // si parte da quantità zero: l'utente indica cosa è stato realmente ricevuto
      this.formService.rows.controls.forEach(row => {
        row.controls.oldQuantity.setValue(row.controls.quantity.value);
        row.controls.quantity.setValue(0);
      });

      this.formService.setDate(new Date());
      this.formService.movementCause.setValue(this.formService.findCauseByValue(STOCK_TRANSFER_CAUSE));
      this.formService.sourceLocationId.setValue(SUPPLIERS_ORDERS_LOCATION_ID);
      this.formService.targetLocationId.setValue(SALES_LOCATION_ID);
    }
  }

  private setupCommonDefaults(movement: any) {
    this.formService.setDate(movement.date);
    this.formService.movementCause.setValue(
      this.formService.findCause(movement.type, movement.load_cause || movement.unload_cause || 'setup')
    );
    this.formService.supplier.setValue(this.dataService.getSupplier(movement.supplier_id));
    this.formService.supplierOrder.setValue(movement.supplier_order ?? null);
    this.formService.notes.setValue(movement.notes ?? null);
  }

  // ------------------- campi -------------------

  protected get rows() {
    return this.formService.rows;
  }

  protected displayEntry = (entry: MovementEntry | string | null): string => {
    return entry && typeof entry === 'object' ? entry._name : (entry ?? '');
  };

  protected displaySupplier = (supplier: any): string => {
    return supplier && typeof supplier === 'object' ? supplier.name : (supplier ?? '');
  };

  protected entryLabel(entry: MovementEntry): string {
    return entry['sku'] ? `${entry['sku']} ${entry._name}` : entry._name;
  }

  protected entryOptions(index: number): MovementEntry[] {
    const value = this.rows.at(index).controls.entry.value;

    if (value && typeof value === 'object') {
      return this.dataService.getEntriesAround(value);
    }

    return this.dataService.filterEntries(typeof value === 'string' ? value : null);
  }

  protected supplierOptions() {
    const value = this.formService.supplier.value;
    return this.dataService.filterSuppliers(typeof value === 'string' ? value : null);
  }

  protected targetLocations() {
    return this.dataService.stockLocations().filter(location => location.id !== this.formService.sourceLocationId.value);
  }

  protected rowValue(index: number): NewMovementRowValue {
    return this.rows.at(index).getRawValue();
  }

  protected entryOf(index: number): MovementEntry | null {
    const entry = this.rowValue(index).entry;
    return entry && typeof entry === 'object' ? entry : null;
  }

  protected stockQuantity(index: number): string | null {
    return this.dataService.getCurrentStockQuantity(this.rowValue(index), this.formService.sourceLocationId.value);
  }

  protected rowAmountLabel(index: number): string {
    return this.tilbyCurrencyPipe.transform(this.dataService.getRowAmount(this.rowValue(index))) ?? '';
  }

  protected onEntryPanelOpened(index: number, autocomplete: MatAutocomplete) {
    setTimeout(() => {
      const options = autocomplete.options?.toArray() ?? [];
      let selectedOption: MatOption | undefined;

      options.forEach(option => {
        if (this.isEntrySelected(index, option.value)) {
          option.select(false);
          selectedOption = option;
        } else {
          option.deselect(false);
        }
      });

      const panel = autocomplete.panel?.nativeElement;

      if (!selectedOption || !panel) return;

      const element = selectedOption._getHostElement();
      panel.scrollTop = Math.max(0, element.offsetTop - element.offsetHeight);
    });
  }

  protected isEntrySelected(index: number, entry: MovementEntry): boolean {
    const selected = this.rows.at(index).controls.entry.value;

    return !!selected && typeof selected === 'object'
      && selected._entryType === entry._entryType
      && selected.id === entry.id
      && (selected.combination_id ?? null) === (entry.combination_id ?? null);
  }

  protected onEntrySelected(index: number) {
    // il prezzo proposto è il costo del prodotto
    this.rows.at(index).controls.price.setValue(this.entryOf(index)?.['cost'] || null);
  }

  protected onEntryBlur(index: number, trigger: MatAutocompleteTrigger) {
    if (!trigger.panelOpen) {
      this.autoSelectFirstEntry(index);
    }
  }

  protected onEntryPanelClosed(index: number, input: HTMLInputElement) {
    if (document.activeElement !== input) {
      this.autoSelectFirstEntry(index);
    }
  }

  private autoSelectFirstEntry(index: number) {
    const control = this.rows.at(index).controls.entry;
    const typedText = control.value;

    if (typeof typedText !== 'string' || !typedText.trim()) return;

    const firstEntry = this.entryOptions(index)[0];

    if (firstEntry) {
      control.setValue(firstEntry);
      this.onEntrySelected(index);
    }
  }

  protected onLastFieldTab(index: number, event: Event) {
    const isLastRow = index === this.rows.length - 1;

    // if (!isLastRow || this.formService.isRowEmpty(this.rowValue(index))) return;
    if (!isLastRow) return;

    event.preventDefault();
    this.addRow();
  }

  // senza prezzo e sconto (modulo prodotti disattivo) l'ultimo campo della riga è la quantità
  protected onQuantityTab(index: number, event: Event) {
    if (!this.dataService.showPriceData) {
      this.onLastFieldTab(index, event);
    }
  }

  protected unitCountLabel(): string {
    const rows = this.rows.getRawValue();
    const count = rows.reduce((sum, row) => sum + Math.abs(Number(row.quantity) || 0), 0);
    const value = rows.reduce((sum, row) => sum + this.dataService.getRowAmount(row), 0);

    return `${this.translateService.instant('STOCK.NEW_MOVEMENT.PIECES')}${count} / ${this.tilbyCurrencyPipe.transform(value)}`;
  }

  protected canSave(): boolean {
    return this.formService.formReady() && this.form.valid && this.formService.hasMovements();
  }

  // ------------------- righe -------------------

  protected addRow() {
    this.formService.addRow();
    // setTimeout(() => this.entryInputs.last?.nativeElement.focus());
  }

  protected async removeRow(index: number) {
    if (!this.formService.isRowEmpty(this.rowValue(index))) {
      const confirmed = await this.confirmDialogService.openDialog({
        data: { messageLabel: 'STOCK.NEW_MOVEMENT.WANT_TO_REMOVE' }
      });

      if (!confirmed) return;
    }

    this.formService.removeRow(index);

    if (!this.rows.length) {
      this.formService.addRow();
    }
  }

  // ------------------- barcode -------------------

  protected async onBarcodeEnter(barcode: string) {
    if (barcode.trim()) {
      await this.onSubmitBarcode(barcode.trim());
    }
  }

  protected async openCameraBarcode() {
    const barcode = await this.dataService.openCameraBarcode();

    if (barcode) {
      this.onSubmitBarcode(barcode);
    }
  }

  private async onSubmitBarcode(barcode: string) {
    const found = await this.dataService.searchBarcode(barcode);

    if (!found.data.length) {
      return this.alertDialogService.openDialog({ data: { messageLabel: 'STOCK.NEW_MOVEMENT.BARCODE_NOT_FOUND' } });
    }

    this.formService.removeEmptyRows();

    for (const foundItem of found.data) {
      if (!foundItem.item_id) continue;

      const key = this.dataService.getItemKey({ combination_id: foundItem.combination_id, item_id: foundItem.item_id });
      const existingRow = this.rows.controls.find(row => {
        const entry = row.controls.entry.value;
        return entry && typeof entry === 'object' && entry._entryType === 'item' && this.dataService.getItemKey(entry) === key;
      });

      if (existingRow) {
        // stesso prodotto già in elenco: si somma la quantità
        existingRow.controls.quantity.setValue(MathUtils.round((existingRow.controls.quantity.value ?? 0) + (foundItem.quantity || 1), 4));
        continue;
      }

      const entry = this.dataService.getItemEntry({ combination_id: foundItem.combination_id, item_id: foundItem.item_id });

      if (entry) {
        if (entry['stock_type'] !== 'simple') {
          await this.alertDialogService.openDialog({ data: { messageLabel: 'STOCK.NEW_MOVEMENT.STOCK_NOT_ENABLED_FOR_SELECTED' } });
        }

        this.formService.addRow({ entry, quantity: foundItem.quantity || 1 });
      }
    }

    if (found.data.length !== found.total) {
      this.alertDialogService.openDialog({ data: { messageLabel: 'STOCK.NEW_MOVEMENT.SOME_ITEMS_NOT_FOUND' } });
    }

    if (!this.rows.length) {
      this.formService.addRow();
    }
  }

  // ------------------- ordine fornitore / ricevimento merce -------------------

  protected get canGenerateSupplierOrder(): boolean {
    return typeof this.formService.supplier.value === 'object'
      && !!this.formService.supplier.value
      && this.formService.movementCause.value?.value === 'supplier_order';
  }

  // popola l'ordine con i prodotti del fornitore sotto la scorta minima
  protected async generateSupplierOrder() {
    const supplier = this.formService.supplier.value;

    if (!supplier || typeof supplier !== 'object') return;

    const supplierItems = this.dataService.entries
      .filter(entry => entry._entryType === 'item' && entry['default_supplier_id'] === supplier.id);

    if (!supplierItems.length) return;

    const surchargeInput = await this.promptDialogService.openDialog({
      data: {
        message: this.translateService.instant('STOCK.NEW_MOVEMENT.SUPPLIER_ORDER_PROMPT_MESSAGE'),
        label: this.translateService.instant('STOCK.NEW_MOVEMENT.SURCHARGE_PERC'),
        type: 'number',
        title: this.translateService.instant('STOCK.NEW_MOVEMENT.SUPPLIER_ORDER_PROMPT_TITLE')
      }
    });

    if (surchargeInput === undefined || surchargeInput === null) return;

    const surchargePerc = Number(surchargeInput) || 0;
    const defaultSupplierOrder = String(this.configurationManagerService.getPreference('stock.supplier_order_progressive' as any) || '1');

    this.formService.defaultSupplierOrder = defaultSupplierOrder;
    this.formService.supplierOrder.setValue(this.formService.supplierOrder.value || defaultSupplierOrder);
    this.formService.movementCause.setValue(this.formService.findCauseByValue('supplier_order'));
    this.formService.sourceLocationId.setValue(SUPPLIERS_ORDERS_LOCATION_ID);
    this.formService.clearRows();

    for (const supplierItem of supplierItems) {
      const quantityToLoad = Math.ceil(
        ((Number(supplierItem['quantity_alert']) || 0) * (1 + (surchargePerc / 100))) - this.dataService.getSalesAndOrdersQuantity(supplierItem)
      );

      if (quantityToLoad > 0) {
        this.formService.addRow({ entry: supplierItem, quantity: quantityToLoad, price: supplierItem['cost'] });
      }
    }

    if (!this.rows.length) {
      this.formService.addRow();
    }
  }

  protected async selectAllGoodsForDelivery() {
    const confirmed = await this.confirmDialogService.openDialog({
      data: { messageLabel: 'STOCK.NEW_MOVEMENT.ALL_GOODS_DELIVERY_CONFIRM' }
    });

    if (!confirmed) return;

    this.rows.controls.forEach(row => {
      if (row.controls.oldQuantity.value) {
        row.controls.quantity.setValue(row.controls.oldQuantity.value);
      }
    });
  }

  // ------------------- salvataggio / navigazione -------------------

  protected async save() {
    this.formService.removeEmptyRows();

    if (!this.rows.length) {
      return this.formService.addRow();
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return this.alertDialogService.openDialog({ data: { messageLabel: 'FORM_ERRORS.THERE_ARE_ERRORS' } });
    }

    let movements: PreparedMovement[];

    try {
      movements = this.saveService.createMovements(this.form, this.formService.mode());
    } catch (error) {
      return this.alertDialogService.openDialog({
        data: { messageLabel: isNewMovementErrorCode(error) ? `STOCK.NEW_MOVEMENT.ERRORS.${error}` : 'STOCK.NEW_MOVEMENT.UNEXPECTED_ERROR' }
      });
    }

    const confirmed = await this.confirmDialogService.openDialog({
      data: { messageLabel: 'STOCK.NEW_MOVEMENT.WANT_TO_SAVE' }
    });

    if (confirmed) {
      await this.sendMovements(movements);
    }
  }

  private async sendMovements(movements: PreparedMovement[]) {
    const sentIndexes = await this.saveService.send(movements);
    const sentMovements = movements.filter(movement => sentIndexes.has(movement.rowIndex));

    // le righe inviate escono dall'elenco, quelle in errore restano per un nuovo tentativo
    for (let i = this.rows.length - 1; i >= 0; i--) {
      if (sentIndexes.has(i)) {
        this.formService.removeRow(i);
      }
    }

    this.saveService.updateItemsCost(sentMovements, id => this.dataService.getOriginalItem(id));

    if (this.rows.length) {
      return this.alertDialogService.openDialog({ data: { messageLabel: 'STOCK.NEW_MOVEMENT.ERROR_IN_SAVE' } });
    }

    this.updateSupplierOrderProgressive();

    if (this.formService.mode() === 'goods_delivery' && this.startGoodsDeliveryAdjustment(sentMovements)) {
      return;
    }

    this.goToMovements();
  }

  // il numero d'ordine proposto è stato usato: il prossimo sarà il successivo
  private updateSupplierOrderProgressive() {
    const defaultOrder = this.formService.defaultSupplierOrder;

    if (defaultOrder && defaultOrder === this.formService.supplierOrder.value) {
      (this.configurationManagerService as any).setShopPreference('stock.supplier_order_progressive', parseInt(defaultOrder, 10) + 1);
    }
  }

  /**
   * Nel ricevimento merce, la differenza tra quantità attesa e ricevuta va registrata come rettifica.
   * @returns true se ci sono righe di rettifica da inviare (la pagina resta aperta)
   */
  private startGoodsDeliveryAdjustment(sentMovements: PreparedMovement[]): boolean {
    for (const { rowValue } of sentMovements) {
      if (rowValue.oldQuantity !== rowValue.quantity) {
        this.formService.addRow({
          ...rowValue,
          quantity: (Number(rowValue.quantity) || 0) - (Number(rowValue.oldQuantity) || 0),
          oldQuantity: null
        });
      }
    }

    this.formService.setMode('goods_delivery_adjustment');
    this.formService.movementCause.setValue(this.formService.findCauseByValue('positive_adjustment'));
    this.formService.sourceLocationId.setValue(SUPPLIERS_ORDERS_LOCATION_ID);
    this.formService.targetLocationId.setValue(null);

    return this.rows.length > 0;
  }

  protected async goBack() {
    if (this.formService.hasMovements()) {
      const confirmed = await this.confirmDialogService.openDialog({
        data: { messageLabel: 'STOCK.NEW_MOVEMENT.WARNING_MOVEMENTS_NOT_SAVED' }
      });

      if (!confirmed) return;
    }

    this.goToMovements();
  }

  private goToMovements() {
    this.state.go(MOVEMENTS_STATE);
  }
}
